import { useState, useEffect, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControl,
    FormGroup,
    FormControlLabel,
    Snackbar,
    Alert,
} from "@mui/material";
import axiosInstance from "../../services/axios-instance";
import { DELETE_USER, EDIT_USER, LOGS_DATE_RANGE ,NATIONAL_HOLIDAYS_WEEKLY, HOLIDAYS_DATE_RANGE, WEEKLY_LOGS } from "../../services/endpoints";
import { AllUsersInterface } from "../../interface/allUsersInterface";
import { Formik, Form, Field, ErrorMessage } from "formik";
import { z } from "zod";
import { ROLES } from "../../assets/data/roles";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { styled } from "@mui/material/styles";
import ResetPassword from "../elements/ResetPassword";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import { userLevels } from "../../interface/userLevels";
import { userAdditionalRoles } from "../../interface/userAdditionalRoles";
import ManageUserClients from "./ManageUserClients";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LockResetIcon from '@mui/icons-material/LockReset';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import ClearIcon from '@mui/icons-material/Clear';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { getUrlWithProfilePicture } from "../../components/utils/getUrlWithProfilePicture";

// TypeScript interfaces for API response structures
interface DailyHours {
    [date: string]: number;
}

interface WeeklyLog {
    user_id: number;
    daily_hours: DailyHours;
}

interface HolidayDateRange {
    from_date: string;
    to_date: string;
}

interface UserHoliday {
    user_id: number;
    holidays: HolidayDateRange[];
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.text.primary,
    position: "relative",
}));

const StyledTableRow = styled(TableRow)(() => ({
    backgroundColor: "white",
}));

const StyledButton = styled(Button)({
    position: 'absolute',
    bottom: '0.5rem',
    padding: "0 0.5rem",
  });

const roles = Object.keys(ROLES)
    .filter((key) => isNaN(Number(key)))
    .map((key) => ({
        value: key,
        key: ROLES[key as keyof typeof ROLES],
    }));

const validationSchema = z.object({
    name: z.string().min(1, { message: "Name is required" }),
    email: z
        .string()
        .email({ message: "Invalid email address" })
        .min(1, { message: "Email is required" }),
});

interface TableUsersParams {
    users: AllUsersInterface[];
    setUsers: (users: AllUsersInterface[]) => void;
    setRefetchUsers: (refetch: boolean) => void;
}

const TableUsers = ({ users, setUsers, setRefetchUsers }: TableUsersParams) => {
    const [removeClient, setRemoveClient] = useState<any[]>([]);
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [deleteUserId, setDeleteUserId] = useState<number | null>(null);
    const [deleteUserName, setDeleteUserName] = useState<string | null>(null);
    const [resetUserId, setResetUserId] = useState<number | null>(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [openRemoveClient, setOpenRemoveClient] = useState(false);
    const [openResetDialog, setOpenResetDialog] = useState(false);
    const [showManageClients, setShowManageClients] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [openAlert, setOpenAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [currentWeekLogs, setCurrentWeekLogs] = useState<WeeklyLog[]>([]);
    const [natHolidays, setNatHolidays] = useState<string[]>([]);
    const [holidays, setHolidays] = useState<UserHoliday[]>([]);
    const [weekOffset, setWeekOffset] = useState<number>(0);
    const [mondayDate, setMondayDate] = useState<string>("");
    const [fridayDate, setFridayDate] = useState<string>("");

    const handleEditClick = (userId: number) => {
        setEditingUserId(userId);
    };

    const handleSaveClick = async (values: AllUsersInterface) => {
        const { id, name, email, role_id, level, additional_roles, birthday } = values;
        const initialUser = users.find((user) => user.id === id);

        // Compare the name, email, and role_id with the initial values
        const editedName = name !== initialUser?.name ? name : undefined;
        const editedEmail = email !== initialUser?.email ? email : undefined;
        const editedBirthday = birthday !== initialUser?.birthday ? birthday : undefined;
        const editedRoleId =
            role_id !== initialUser?.role_id ? role_id : undefined;
        const editedLevel = level !== initialUser?.level ? level : undefined;
        const editedAdditionalRoles =
            JSON.stringify(additional_roles) !==
            JSON.stringify(initialUser?.additional_roles)
                ? additional_roles
                : undefined;

        const requestBody: any = {};
        if (editedName) requestBody.name = editedName;
        if (editedEmail) requestBody.email = editedEmail;
        if (editedBirthday) requestBody.birthday = editedBirthday;
        if (editedRoleId) requestBody.role_id = editedRoleId;
        if (editedLevel) requestBody.level = editedLevel;
        if (editedAdditionalRoles)
            requestBody.additional_roles = editedAdditionalRoles;

        const response = await axiosInstance.post(EDIT_USER(id), requestBody);

        if (response.status !== 200) {
            console.error("Error updating user");
            return;
        }

        // Update the user with the edited values
        const updatedUsers = users.map((user) => {
            if (user.id === values.id) {
                return values;
            }
            return user;
        });
        // Save the updated users array
        setUsers(updatedUsers);
        // show alert for success
        setAlertMessage("User updated successfully");
        setOpenAlert(true);
        // Clear the editing state
        setEditingUserId(null);
    };

    const handleDeleteClick = (userId: number, userName: string) => {
        setDeleteUserId(userId);
        setDeleteUserName(userName);
        setOpenDeleteDialog(true);
    };

    const handleRemoveClientClick = (userId: number, clientId: number) => {
        setRemoveClient([userId,clientId])
        setOpenRemoveClient(true);
    };

    const handleDeleteConfirm = async () => {
        if (deleteUserId !== null) {
            const response = await axiosInstance.delete(
                DELETE_USER(deleteUserId)
            );

            if (response.status !== 200) {
                console.error("Error deleting user");
                return;
            }
            const updatedUsers = users.filter(
                (user) => user.id !== deleteUserId
            );

            setUsers(updatedUsers);

            setAlertMessage("User deleted successfully");
            setOpenAlert(true);
        }
        // Clear the delete state
        setDeleteUserId(null);
        setDeleteUserName(null);
        setOpenDeleteDialog(false);
    };

    const handleRemoveClientConfirm = async () => {
            const response = await axiosInstance.delete(
                'users/'+removeClient[0]+'/clients/'+removeClient[1]
            );

            if (response.status !== 200) {
                console.error("Error deleting user");
                return;
            }
            const updatedUsers = users.map(user => {
                if (user.id === removeClient[0] && user.clients) {
                  return {
                    ...user,
                    clients: user.clients.filter(client => client.id !== removeClient[1])
                  };
                }
                return user;
              });

            setUsers(updatedUsers);

        setRemoveClient([]);
        setOpenRemoveClient(false);
    };

    const handleDeleteCancel = () => {
        // Clear the delete state
        setDeleteUserId(null);
        setDeleteUserName(null);
        setOpenDeleteDialog(false);
    };

    const handleRemoveClientCancel = () => {
        setOpenRemoveClient(false);
    };

    const handleResetClick = (userId: number) => {
        setResetUserId(userId);
        setOpenResetDialog(true);
    };

    const getTimelogs = (userId: number) => {
        window.location.href = "timelog/user/" + userId;
    };

    const handleResetCancel = () => {
        setResetUserId(null);
        setOpenResetDialog(false);
    };

    const handleManageClients = (userId: number) => {
        setSelectedUserId(userId);
        setShowManageClients(true);
    };

    const handleCloseManageClients = () => {
        setShowManageClients(false);
        setSelectedUserId(null);
    };

    // Helper function to get hours for a specific user and date
    const getHoursForUserAndDate = (userId: number, date: string): number | undefined => {
        const userLog = currentWeekLogs.find(log => log.user_id === userId);
        return userLog?.daily_hours[date];
    };

    // Helper function to get holidays for a specific user
    const getHolidaysForUser = (userId: number): HolidayDateRange[] => {
        const userHoliday = holidays.find(h => h.user_id === userId);
        return userHoliday?.holidays || [];
    };

    const dotClassName = 'dot'; // static class name

    const weekdayNames = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
    ];

    const today = new Date().getDay()-1;

    function formatDate(date: Date): string {
        const year = date.getFullYear();
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const day = ('0' + date.getDate()).slice(-2);
        return `${year}-${month}-${day}`;
    }

    function isDateInHolidayRange(holidays: HolidayDateRange[], dateStr: string): boolean {
        if (!holidays || holidays.length === 0) {
            return false;
        }

        const checkDate = new Date(dateStr);
        return holidays.some(holiday => {
            const startDate = new Date(holiday.from_date);
            const endDate = new Date(holiday.to_date);
            return checkDate >= startDate && checkDate <= endDate;
        });
    }

    // Calculate week dates based on offset (pure function - no side effects)
    const calculateWeekDates = (offset: number) => {
        const currentDate = new Date();
        const currentDay = currentDate.getDay();

        // Calculate the date for Monday of the current week
        const monday = new Date(currentDate);
        monday.setDate(currentDate.getDate() - currentDay + (currentDay === 0 ? -6 : 1) + (offset * 7));

        // Calculate the date for Friday of the current week
        const friday = new Date(monday);
        friday.setDate(monday.getDate() + 4);

        return {
            monday: monday,
            friday: friday
        };
    };

    // Memoize week dates to prevent recalculation on every render
    const weekDates = useMemo(() => calculateWeekDates(weekOffset), [weekOffset]);

    // Update display dates when weekDates changes
    useEffect(() => {
        setMondayDate(weekDates.monday.toISOString().split('T')[0]);
        setFridayDate(weekDates.friday.toISOString().split('T')[0]);
    }, [weekDates]);

    const nextWeek = () => {
        setWeekOffset(weekOffset + 1);
    };

    const prevWeek = () => {
        setWeekOffset(weekOffset - 1);
    };
    
    useEffect(() => {
        const fetchWeeklyLogs = async () => {
            try {
                let response;

                if (weekOffset !== 0) {
                    const from = new Date(weekDates.monday);
                    const to = new Date(weekDates.friday);

                    response = await axiosInstance.get(
                        LOGS_DATE_RANGE,
                        {
                            params: { from, to }
                        }
                    );
                } else {
                    response = await axiosInstance.get(WEEKLY_LOGS);
                }

                // Laravel Resource returns { data: [...] }
                setCurrentWeekLogs(response.data.data || []);
            } catch (error) {
                console.error('Error fetching weekly logs:', error);
            }
        };

        const fetchNationalHolidays = async () => {
            try {
                const from = new Date(weekDates.monday);
                from.setDate(from.getDate() - 1);
                const to = weekDates.friday;

                const response = await axiosInstance.get(
                    NATIONAL_HOLIDAYS_WEEKLY,
                    {
                        params: { from, to }
                    }
                );

                const holidaysList = response.data.map((obj: { date: string }) => obj.date);
                setNatHolidays(holidaysList);
            } catch (error) {
                console.error('Error fetching national holidays:', error);
            }
        };

        const fetchHolidays = async () => {
            try {
                const from = new Date(weekDates.monday);
                const to = new Date(weekDates.friday);

                const response = await axiosInstance.get(
                    HOLIDAYS_DATE_RANGE,
                    {
                        params: { from, to }
                    }
                );

                // Laravel Resource returns { data: [...] }
                setHolidays(response.data.data || []);
            } catch (error) {
                console.error('Error fetching holidays:', error);
            }
        };

        fetchWeeklyLogs();
        fetchNationalHolidays();
        fetchHolidays();
    }, [weekOffset]); // Only depend on weekOffset, weekDates is derived from it

    return (
        <>
            <TableContainer component={Paper}>
                <Formik
                    initialValues={
                        editingUserId
                            ? users.find(
                                  (user) => user.id === editingUserId
                              ) || {
                                  id: 0,
                                  name: "",
                                  email: "",
                                  role_id: 0,
                                  status: 0,
                                  level: "",
                                  additional_roles: [],
                                  birthday: "",
                                  profile_picture: null,
                                  profile_picture_focal_point: null,
                                  public_logs: false,
                              }
                            : {
                                  id: 0,
                                  name: "",
                                  email: "",
                                  role_id: 0,
                                  status: 0,
                                  level: "",
                                  additional_roles: [],
                                  birthday: "",
                                  profile_picture: null,
                                  profile_picture_focal_point: null,
                                  public_logs: false,
                              }
                    }
                    validationSchema={toFormikValidationSchema(
                        validationSchema
                    )}
                    onSubmit={handleSaveClick}
                    enableReinitialize
                >
                    {({ values }) => (
                        <Form id="formik-form">
                            <Table>
                                <TableHead>
                                    <TableRow
                                        sx={{
                                            backgroundColor: "#f9fafc",
                                        }}
                                    >
                                        <StyledTableCell>Picture</StyledTableCell>
                                        <StyledTableCell>No.</StyledTableCell>
                                        <StyledTableCell>DB ID</StyledTableCell>
                                        <StyledTableCell>Name</StyledTableCell>
                                        <StyledTableCell>Email</StyledTableCell>
                                        <StyledTableCell>Birthday</StyledTableCell>
                                        <StyledTableCell>Role</StyledTableCell>
                                        <StyledTableCell>Level</StyledTableCell>
                                        <StyledTableCell>
                                            Additional Roles
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            Actions
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            Clients
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            <div>
                                                <NavigateBeforeIcon
                                                    style={{
                                                        marginBottom: '-8px',
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={prevWeek}>
                                                </NavigateBeforeIcon>
                                                    Weekly Report
                                                <NavigateNextIcon
                                                    style={{
                                                        marginBottom: '-8px',
                                                        cursor: "pointer",
                                                    }}
                                                    onClick={nextWeek}>
                                                </NavigateNextIcon>
                                            </div>
                                            <div
                                            style={{
                                                width: "fit-content",
                                                margin: "auto"
                                            }}>
                                                <span>{mondayDate} - </span>
                                                <br/>
                                                <span>{fridayDate}</span>
                                            </div>
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            Timelog
                                        </StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {users.map((user, index) => (
                                        <StyledTableRow key={user.id}>
                                            <StyledTableCell>
                                            <img
                                                style={{
                                                    height: 70,
                                                    width: 70,
                                                    objectFit: "cover",
                                                    objectPosition: user?.profile_picture_focal_point ? user?.profile_picture_focal_point : "center",
                                                    borderRadius: "50%"
                                                }}
                                                src={getUrlWithProfilePicture(user?.profile_picture)}
                                            />
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {index+1}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {user.id}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        type="text"
                                                        name="name"
                                                        as={TextField}
                                                    />
                                                ) : (
                                                    user.name
                                                )}

                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="name"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        type="email"
                                                        name="email"
                                                        as={TextField}
                                                    />
                                                ) : (
                                                    user.email
                                                )}
                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="email"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                <Field
                                                    type="date"
                                                    name="birthday"
                                                    as={TextField}
                                                /> 
                                                ) : (
                                                    user.birthday
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        name="role_id"
                                                        as={Select}
                                                        value={values.role_id}
                                                    >
                                                        {roles.map((role) => (
                                                            <MenuItem
                                                                key={role.key}
                                                                value={role.key}
                                                            >
                                                                {role.value}
                                                            </MenuItem>
                                                        ))}
                                                    </Field>
                                                ) : (
                                                    roles.find(
                                                        (role) =>
                                                            role.key ===
                                                            user.role_id
                                                    )?.value
                                                )}
                                                {editingUserId === user.id && (
                                                    <ErrorMessage
                                                        name="role_id"
                                                        component="div"
                                                    />
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <Field
                                                        type="text"
                                                        name="level"
                                                        as={Select}
                                                        value={values.level}
                                                    >
                                                        {userLevels.map(
                                                            (level) => (
                                                                <MenuItem
                                                                    key={level}
                                                                    value={
                                                                        level
                                                                    }
                                                                >
                                                                    {level}
                                                                </MenuItem>
                                                            )
                                                        )}
                                                    </Field>
                                                ) : (
                                                    user.level
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                {editingUserId === user.id ? (
                                                    <FormControl component="fieldset">
                                                        <FormGroup>
                                                            {userAdditionalRoles.map(
                                                                (role) => (
                                                                    <FormControlLabel
                                                                        key={
                                                                            role.key
                                                                        }
                                                                        control={
                                                                            <Field
                                                                                as={
                                                                                    Checkbox
                                                                                }
                                                                                type="checkbox"
                                                                                name="additional_roles"
                                                                                value={
                                                                                    role.key
                                                                                }
                                                                            />
                                                                        }
                                                                        label={
                                                                            role.label
                                                                        }
                                                                    />
                                                                )
                                                            )}
                                                        </FormGroup>
                                                    </FormControl>
                                                ) : (
                                                    user.additional_roles
                                                        ?.map(
                                                            (role) =>
                                                                userAdditionalRoles.find(
                                                                    (r) =>
                                                                        r.key ===
                                                                        role
                                                                )?.label
                                                        )
                                                        .join(", ")
                                                )}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <div style={{ display: "inline-flex" }}>
                                                {editingUserId === user.id ? (
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        type="submit"
                                                        form="formik-form"
                                                    >
                                                        Save
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        onClick={() =>
                                                            handleEditClick(
                                                                user.id
                                                            )
                                                        }
                                                    >
                                                        <EditIcon></EditIcon>
                                                    </Button>
                                                )}
                                                <Button
                                                    onClick={() =>
                                                        handleDeleteClick(
                                                            user.id,
                                                            user.name
                                                        )
                                                    }
                                                >
                                                    <DeleteIcon></DeleteIcon>
                                                </Button>
                                                <Button
                                                    onClick={() =>
                                                        handleResetClick(
                                                            user.id
                                                        )
                                                    }
                                                >
                                                    <LockResetIcon></LockResetIcon>
                                                    </Button>
                                                </div>
                                            </StyledTableCell>
                                            <StyledTableCell style={{ minWidth: '165px' }}>
                                                {user.clients && user.clients?.length > 0 && (
                                                    <div 

                                                    style={{ marginBottom: "1.5rem" }}>
                                                    {user.clients.map((client) => (
                                                        <div 
                                                        onClick={() =>
                                                            handleRemoveClientClick(
                                                                user.id,
                                                                client.id
                                                            )
                                                        }
                                                        style={{display:"flex", alignItems: "center", cursor: "pointer"}} key={client.id}><ClearIcon></ClearIcon> {client.name}: {client.pivot.hours}h</div>
                                                    ))}
                                                    </div>
                                                )}
                                                <StyledButton
                                                    color="info"
                                                    variant="contained"
                                                    onClick={() =>
                                                        handleManageClients(
                                                            user.id
                                                        )
                                                    }
                                                >
                                                    Manage Clients
                                                </StyledButton>
                                            </StyledTableCell>
                                            <StyledTableCell style={{ minWidth: '180px' }}>
                                            {weekdayNames.map((weekday) => {
                                                const currentDate = new Date(weekDates.monday);
                                                currentDate.setDate(currentDate.getDate() + weekdayNames.indexOf(weekday));
                                                const dateStr = formatDate(currentDate);
                                                const totalHours = getHoursForUserAndDate(user.id, dateStr);
                                                const userHolidays = getHolidaysForUser(user.id);
                                                const isNationalHoliday = natHolidays.includes(dateStr);
                                                const isOnHoliday = isDateInHolidayRange(userHolidays, dateStr);

                                                let status = "";
                                                const isPastOrToday = (today >= weekdayNames.indexOf(weekday) && weekOffset === 0) || weekOffset < 0;

                                                if (isPastOrToday) {
                                                    if (!totalHours) {
                                                        status = "insufficient";
                                                    } else if (totalHours < 8) {
                                                        status = "insufficient";
                                                    } else if (totalHours > 8) {
                                                        status = "overtime";
                                                    } else {
                                                        status = "success";
                                                    }
                                                }

                                                if (isNationalHoliday) {
                                                    status = "nat_holiday";
                                                } else if (isOnHoliday) {
                                                    status = "holiday";
                                                }

                                                return (
                                                    <span key={weekday} className={`${dotClassName} ${weekday} ${status}`}>
                                                        {isNationalHoliday ? (
                                                            <img src="/flag-for-flag-north-macedonia.svg" alt="National Holiday" />
                                                        ) : isOnHoliday ? (
                                                            <BeachAccessIcon />
                                                        ) : ""}
                                                    </span>
                                                );
                                            })}
                                            </StyledTableCell>
                                            <StyledTableCell>
                                                <Button
                                                    onClick={() =>
                                                        getTimelogs(user.id)
                                                    }
                                                >
                                                    <ExitToAppOutlinedIcon />
                                                </Button>
                                            </StyledTableCell>
                                            <Dialog
                                                open={
                                                    openResetDialog &&
                                                    user.id == resetUserId
                                                }
                                            >
                                                <DialogTitle>
                                                    New Password
                                                </DialogTitle>
                                                <ResetPassword
                                                    onClose={handleResetCancel}
                                                    userId={user.id}
                                                    setAlertMessage={
                                                        setAlertMessage
                                                    }
                                                    setOpenAlert={setOpenAlert}
                                                />
                                            </Dialog>
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Form>
                    )}
                </Formik>
                <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
                    <DialogTitle>Confirm Deactivate</DialogTitle>
                    <DialogContent>
                        Are you sure you want to deactivate {deleteUserName}?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="secondary">
                            Deactivate
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog open={openRemoveClient} onClose={handleRemoveClientCancel}>
                    <DialogTitle>Confirm Client Unassign</DialogTitle>
                    <DialogContent>
                        Are you sure you want to unassign this client?
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleRemoveClientCancel} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={handleRemoveClientConfirm} color="secondary">
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>
            </TableContainer>
            {showManageClients && selectedUserId !== null && (
                <ManageUserClients
                    userId={selectedUserId}
                    userName={
                        users.find((user) => user.id === selectedUserId)?.name
                    }
                    onClose={handleCloseManageClients}
                    setRefetchUsers={setRefetchUsers}
                />
            )}
            <Snackbar
                open={openAlert}
                autoHideDuration={2000}
                onClose={() => setOpenAlert(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    elevation={6}
                    variant="filled"
                    onClose={() => setOpenAlert(false)}
                    severity="success"
                >
                    {alertMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default TableUsers;
